// Copyright (c) Docugami, Inc. All rights reserved.

import { DndType, DndChunk } from "components/cards/DndTypes";
import { ChunkInstance } from "networking/api";
import { useEffect } from "react";
import { ConnectDragSource, useDrag } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";
import { ComponentId } from "sdv/engine/Component";
import { Engine } from "sdv/engine/Engine";
import { ActionId } from "sdv/input/Actions";
import { DgmlInstancesSingleton, useDocumentDgml } from "./documentDgml";
import { useLabelChunk } from "./LabelChunk";
import { useSdvEntityQuery } from "./SdvEntityQuery";
import { TokenRangeComponent } from "sdv/components/TokenRangeComponent";

const componentIds = [ComponentId.ChunkDrag, ComponentId.TokenRange];

export interface ISdvEntityQuery {
    dragConnectSource: ConnectDragSource;
    isDragging: boolean;
    item: DndChunk | undefined;
}

export function useSdvDnd(docSetId: string, documentId: string, engine: Engine | null, canDrag: (instance: ChunkInstance | null, label?: string) => boolean = () => true): ISdvEntityQuery {
    const { entities } = useSdvEntityQuery(componentIds, engine);
    const { getChunkLabel } = useLabelChunk(docSetId, documentId);
    const { isInitialized } = useDocumentDgml(documentId);

    const [{ isDragging, item }, dragRef, dragPreviewRef] = useDrag(() => {
        let label = '';
        let instance: ChunkInstance | null = null;
        const point = engine?.inputController?.input.getAxis2d(ActionId.SelectCursor) ?? null;
        const entity = entities[0];
        if (!!entity && isInitialized()) {
            instance = DgmlInstancesSingleton.getInstanceById(documentId, entity.name);
            if (!instance) {
                const tokenRange = entity.getComponent<TokenRangeComponent>(ComponentId.TokenRange)?.tokenRange;
                if (!!tokenRange) {
                    instance = DgmlInstancesSingleton.getInstanceById(documentId, DgmlInstancesSingleton.getSpanIdForTokenRange(documentId, tokenRange));
                }
            }

            label = !!instance ? getChunkLabel(instance) : '';
        }

        return {
            type: DndType.Chunk,
            item: () => {
                if (!!instance) {
                    return {
                        type: DndType.Chunk,
                        instance: instance,
                        label: label
                    }
                }

                return null;
            },
            canDrag: () => canDrag(instance, label),
            collect: (m) => ({
                isDragging: m.isDragging(),
                item: m.isDragging() ? m.getItem() as DndChunk | undefined : undefined
            }),
            previewOptions: {
                offsetX: point?.x,
                offsetY: point?.y
            }
        }
    }, [entities, getChunkLabel, documentId, engine, canDrag]);

    useEffect(() => {
        dragPreviewRef(getEmptyImage());
    }, [dragPreviewRef, entities, getChunkLabel, documentId, engine, canDrag]);

    return {
        dragConnectSource: dragRef,
        isDragging: isDragging,
        item: item
    }
}
